<template>
  <b-card class="career-card">
    <div class="d-flex align-items-baseline justify-content-between flex-wrap mb-1">
      <h4 class="mb-0">Sua carreira</h4>
      <span v-if="progress && progress.actuation_label" class="career-actuation-wrap">
        <small class="career-section-label">Atuação</small>
        <span class="career-actuation">{{ progress.actuation_label }}</span>
      </span>
    </div>

    <div v-if="loading" class="d-flex justify-content-center py-2">
      <b-spinner variant="primary" />
    </div>

    <p v-else-if="!progress" class="text-muted mb-0">
      Não foi possível carregar a carreira agora. Tente novamente em instantes.
    </p>

    <template v-else>
      <!--
        A lacuna vem primeiro e com nome: "Faltam R$ 6.500 para alcançar Hunter 3". Um percentual
        sobre dois itens nao informa nada que o checklist abaixo ja nao mostre.
      -->
      <div class="career-gap" :class="{ quiet: gap.quiet }">
        <p class="career-gap-text mb-0">
          <template v-for="(parte, index) in gap.parts">
            <strong v-if="parte.strong" :key="index" class="career-amount">{{ parte.text }}</strong>
            <template v-else>{{ parte.text }}</template>
          </template>
        </p>
        <small v-if="gap.sub" class="text-muted">{{ gap.sub }}</small>
      </div>

      <!-- A escada inteira da atuacao: e o que faz os marcos pequenos aparecerem. -->
      <div v-if="ladder.length" ref="track" class="career-track">
        <div
          v-for="degrau in ladder"
          :key="degrau.code"
          :ref="degrau.state === 'current' || degrau.state === 'next' ? 'focus' : null"
          class="career-step"
          :class="degrau.state"
          :style="degrau.fill === null ? null : { '--career-fill': degrau.fill + '%' }"
        >
          <span class="career-dot" />
          <span class="career-step-label">
            {{ degrau.name }}
            <span class="career-step-code">{{ degrau.matrixCode }}</span>
          </span>
        </div>
      </div>

      <!-- A frase acima ja diz O QUE e; aqui vai so o porque, sem repetir a oracao. -->
      <p v-if="!progress.actuation_key" class="text-muted mt-1 mb-0">
        Este nível de comissionamento não está mapeado em nenhuma atuação da matriz aprovada,
        então não há subníveis a percorrer.
      </p>

      <template v-else-if="progress.next">
        <b-alert v-if="revenueIsStale" variant="warning" show class="p-1 mt-1 mb-0">
          <small>
            Não foi possível consultar o faturamento acumulado agora. Os valores abaixo são os da
            última apuração e podem estar desatualizados.
          </small>
        </b-alert>

        <!-- Medidor so para criterio numerico, onde percentual significa alguma coisa. -->
        <div v-for="(item, index) in numericItems" :key="'m' + index" class="career-meter mt-1">
          <div class="d-flex justify-content-between align-items-baseline">
            <small class="text-muted">{{ item.label }}</small>
            <small class="career-values">
              {{ item.current | toCurrency }}
              <span class="text-muted">/ {{ item.target | toCurrency }}</span>
            </small>
          </div>
          <b-progress
            :value="percentOf(item)"
            max="100"
            :variant="item.satisfied ? 'success' : 'primary'"
            height="6px"
            class="mt-25"
          />
        </div>

        <b-list-group v-if="checklistItems.length" flush class="mt-1">
          <b-list-group-item
            v-for="(item, index) in checklistItems"
            :key="index"
            class="px-0 py-50 border-0"
          >
            <div class="d-flex align-items-start">
              <feather-icon
                :icon="item.satisfied ? 'CheckCircleIcon' : 'CircleIcon'"
                size="16"
                class="mr-50 mt-25 flex-shrink-0"
                :class="item.satisfied ? 'text-success' : 'text-muted'"
              />
              <span :class="item.satisfied ? 'text-muted' : ''">{{ item.label }}</span>
            </div>

            <!-- Exigencias alternativas da matriz: caminhos, nao pendencias somadas. -->
            <div v-if="item.options" class="career-alt mt-50">
              <div
                v-for="(option, optionIndex) in item.options"
                :key="optionIndex"
              >
                <small
                  v-if="optionIndex > 0"
                  class="career-alt-sep d-block my-25"
                >ou</small>
                <div v-for="(subItem, subIndex) in option.items" :key="subIndex">
                  <div class="d-flex justify-content-between align-items-start">
                    <span :class="subItem.satisfied ? 'text-success' : 'text-muted'">
                      <feather-icon
                        :icon="subItem.satisfied ? 'CheckCircleIcon' : 'CircleIcon'"
                        size="14"
                        class="mr-50"
                      />
                      {{ subItem.label }}
                    </span>
                    <small v-if="isRevenue(subItem)" class="text-muted career-values">
                      {{ subItem.current | toCurrency }} / {{ subItem.target | toCurrency }}
                    </small>
                  </div>

                  <!--
                    PNC 1 e o primeiro degrau da escada do Closer: nao existe traco a esquerda
                    para preencher, e o faturamento dele mora dentro do "ou" da matriz, fora do
                    medidor de cima. Sem esta barra o Closer a caminho dos R$ 50.000 nao tinha
                    nenhuma leitura visual de quanto falta -- so os dois numeros ao lado.
                  -->
                  <b-progress
                    v-if="isRevenue(subItem)"
                    :value="percentOf(subItem)"
                    max="100"
                    :variant="subItem.satisfied ? 'success' : 'primary'"
                    height="4px"
                    class="mt-25 mb-50"
                  />
                </div>
              </div>
            </div>
          </b-list-group-item>
        </b-list-group>
      </template>

      <!--
        Entre cumprir o criterio e a rotina diaria gravar, o card (ao vivo) e a ficha (materializada)
        discordam. Dizer isso custa uma linha e evita o chamado "ja tenho o certificado e nao subi".
      -->
      <div v-if="progress.pending_sync && progress.actuation_key" class="career-notice mt-1">
        <feather-icon icon="ClockIcon" size="14" class="mr-50 flex-shrink-0" />
        <small>
          <strong>O enquadramento roda todo dia às 5h.</strong>
          O subnível é atualizado na próxima execução.
        </small>
      </div>

      <template v-if="achievements.length">
        <hr />
        <small class="career-section-label d-block mb-50"> Marcos conquistados </small>
        <div class="career-pills">
          <span v-for="marco in achievements" :key="marco.code" class="career-pill">
            <span class="career-pill-dot" />
            {{ marco.commercial_label || marco.label }}
            <time class="text-muted">{{ marco.achieved_at | onlyDate }}</time>
          </span>
        </div>
      </template>
    </template>
  </b-card>
</template>

<script>
import {
  BCard,
  BSpinner,
  BProgress,
  BListGroup,
  BListGroupItem,
  BAlert,
} from "bootstrap-vue";
import {
  describeCriteriaItem,
  CRITERIA_REVENUE_MIN,
  CRITERIA_ROLE_IN,
} from "@/constants/sub_role_criteria_types";
import { buildCareerGap } from "@/modules/consultant/utils/careerGap";

export default {
  components: {
    BCard,
    BSpinner,
    BProgress,
    BListGroup,
    BListGroupItem,
    BAlert,
  },
  props: {
    progress: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    nextChecklist: function () {
      if (!this.progress || !this.progress.next_items) return [];
      return this.progress.next_items
        .map(describeCriteriaItem)
        .filter((item) => item !== null);
    },

    // O medidor sobe para o topo porque faturamento e o unico criterio em que uma barra
    // significa alguma coisa; o resto e concluido ou pendente.
    numericItems: function () {
      return this.nextChecklist.filter((item) => this.isRevenue(item));
    },

    checklistItems: function () {
      return this.nextChecklist.filter((item) => {
        // Faturamento tem medidor proprio, com os valores.
        if (this.isRevenue(item)) return false;

        // Nivel de comissionamento cumprido e pre-condicao da escada, nao marco: nao da para
        // estar na trilha do Closer sem ser PNC. Ocupava uma linha para informar o que a
        // propria existencia da trilha ja informa. Pendente ele continua aparecendo -- ai e
        // informacao de verdade ("precisa ser promovido antes").
        if (item.type === CRITERIA_ROLE_IN && item.satisfied) return false;

        return true;
      });
    },

    achievements: function () {
      if (!this.progress || !this.progress.achievements) return [];
      return this.progress.achievements;
    },

    // Quando o backend-kpi nao responde, o faturamento cai para o valor da ultima apuracao.
    revenueIsStale: function () {
      return Boolean(this.progress) && this.progress.revenue_is_live === false;
    },

    /**
     * Quanto do trecho ate o proximo degrau ja foi percorrido, em %.
     *
     * So existe quando ha criterio numerico: distancia so se mede onde ha numero. Pendencia de
     * certificado nao tem meio caminho -- ou tem, ou nao tem -- e ali o traco fica cinza, que e
     * a leitura honesta.
     */
    percentToNext: function () {
      return this.melhorPercentual(this.nextChecklist);
    },

    /**
     * A escada da atuacao, com o estado de cada degrau:
     *   done     ja conquistado (tem medalha)
     *   current  o subnivel de agora
     *   next     o proximo marco
     *   future   ainda distante
     */
    ladder: function () {
      if (!this.progress || !this.progress.ladder) return [];

      const conquistados = this.achievements.map((marco) => marco.code);
      const atual = this.progress.current && this.progress.current.code;
      const proximo = this.progress.next && this.progress.next.code;
      const ordemAtual = this.progress.current
        ? this.progress.current.sort_order
        : null;

      return this.progress.ladder.map((degrau) => {
        let state = "future";
        if (degrau.code === atual) state = "current";
        else if (degrau.code === proximo) state = "next";
        else if (conquistados.indexOf(degrau.code) !== -1) state = "done";
        // Degrau abaixo do atual sem medalha: o PN passou por ele sem conquistá-lo, porque a
        // escada o travava pelo nível de comissionamento -- um FOCADO nunca foi FA 1 nem FA 2.
        // Ficava "future", cinza igual aos degraus à frente, e a trilha nascia com buraco.
        else if (ordemAtual !== null && degrau.sort_order < ordemAtual) state = "past";

        return {
          code: degrau.code,
          name: degrau.commercial_label || degrau.label,
          matrixCode: degrau.label,
          state,
          fill: state === "next" ? this.percentToNext : null,
        };
      });
    },

    /**
     * A frase que nomeia a lacuna. A regra vive em utils/careerGap, que tem teste.
     */
    gap: function () {
      return buildCareerGap(
        this.progress,
        this.nextChecklist,
        this.$options.filters.toCurrency
      );
    },
  },
  watch: {
    // Em telefone a escada de 5 degraus nao cabe e rola de lado. Sem isto ela abre no comeco e
    // o PN nao ve onde esta. Centraliza o degrau atual -- ou o proximo, para quem ainda nao foi
    // enquadrado -- dentro do proprio container, sem mexer na rolagem da pagina.
    progress: {
      handler: function () {
        this.$nextTick(this.focarDegrauAtual);
      },
      immediate: true,
    },
  },
  methods: {
    focarDegrauAtual: function () {
      const trilha = this.$refs.track;
      const alvo = this.$refs.focus && this.$refs.focus[0];

      if (!trilha || !alvo || trilha.scrollWidth <= trilha.clientWidth) return;

      trilha.scrollLeft =
        alvo.offsetLeft - trilha.clientWidth / 2 + alvo.offsetWidth / 2;
    },

    isRevenue: function (item) {
      return item.type === CRITERIA_REVENUE_MIN;
    },

    /**
     * O maior percentual numerico que existir na pendencia, inclusive dentro de um grupo "ou".
     *
     * O medidor com os valores so aparece para criterio numerico de primeiro nivel -- dentro do
     * "ou" os numeros ja saem ao lado da alternativa, e repeti-los em cima seria dizer a mesma
     * coisa duas vezes. Mas o traco da trilha precisa saber: em PNC 1 o faturamento mora dentro
     * do "ou" da matriz, e sem isto a trilha ficava cinza para um Closer que ja esta em 76% da
     * rota do faturamento. Entre alternativas vale a rota mais adiantada, porque basta uma.
     */
    melhorPercentual: function (items) {
      let melhor = null;

      const considerar = (valor) => {
        if (valor === null) return;
        melhor = melhor === null ? valor : Math.max(melhor, valor);
      };

      (items || []).forEach((item) => {
        if (this.isRevenue(item)) {
          considerar(this.percentOf(item));
          return;
        }

        (item.options || []).forEach((option) => {
          considerar(this.melhorPercentual(option.items));
        });
      });

      return melhor;
    },

    percentOf: function (item) {
      if (!item.target) return 0;
      const pct = Math.round(((item.current || 0) / item.target) * 100);
      return Math.min(100, Math.max(0, pct));
    },
  },
};
</script>

<style scoped>
.career-actuation-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.career-actuation {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(130, 134, 139, 0.12);
  white-space: nowrap;
}

.career-gap p {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.015em;
}
.career-gap.quiet p {
  font-size: 15px;
  font-weight: 500;
  opacity: 0.75;
}
.career-amount {
  color: var(--primary, #7367f0);
  font-variant-numeric: tabular-nums;
}

.career-values {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  white-space: nowrap;
}

/* trilha */
.career-track {
  display: flex;
  align-items: flex-start;
  overflow-x: auto;
  margin-top: 14px;
  padding-bottom: 4px;
}
.career-step {
  position: relative;
  flex: 1 1 0;
  min-width: 68px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.career-step::before {
  content: "";
  position: absolute;
  top: 7px;
  left: -50%;
  width: 100%;
  height: 2px;
  background: rgba(130, 134, 139, 0.3);
}
.career-step:first-child::before {
  display: none;
}
.career-step.done::before,
.career-step.past::before,
.career-step.current::before {
  background: #28c76f;
}
/* O traco ate o proximo degrau carrega a mesma distancia que o medidor numerico mostra logo
   abaixo. Sem isto a trilha ficava cinza -- 0% -- enquanto a barra dizia 76%: duas versoes da
   mesma viagem, e perguntaram qual das duas estava certa. O ponto continua tracejado de
   proposito: o traco mede distancia percorrida, o ponto diz se chegou. */
.career-step.next::after {
  content: "";
  position: absolute;
  top: 7px;
  left: -50%;
  width: var(--career-fill, 0%);
  height: 2px;
  background: #28c76f;
}
.career-step.next:first-child::after {
  display: none;
}
/* Percorrido, mas sem medalha: ponto cheio e apagado -- nem verde de conquista, nem o cinza
   vazado dos degraus que ainda estao a frente. */
.career-step.past .career-dot {
  background: rgba(130, 134, 139, 0.6);
}
.career-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(130, 134, 139, 0.35);
  position: relative;
  z-index: 1;
  flex: none;
}
.career-step.done .career-dot {
  background: #28c76f;
}
.career-step.current .career-dot {
  background: var(--primary, #7367f0);
  box-shadow: 0 0 0 4px rgba(115, 103, 240, 0.2);
}
.career-step.next .career-dot {
  background: transparent;
  border: 2px dashed var(--primary, #7367f0);
}
.career-step-label {
  font-size: 11.5px;
  line-height: 1.3;
  text-align: center;
  opacity: 0.6;
}
.career-step.done .career-step-label,
.career-step.past .career-step-label,
.career-step.next .career-step-label {
  opacity: 0.9;
}
.career-step.current .career-step-label {
  opacity: 1;
  font-weight: 700;
}
.career-step-code {
  display: block;
  font-size: 10px;
  opacity: 0.6;
  font-variant-numeric: tabular-nums;
}

.career-alt {
  border-left: 2px solid rgba(130, 134, 139, 0.25);
  margin-left: 7px;
  padding-left: 14px;
}
.career-alt-sep {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.6;
}

.career-notice {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  background: rgba(255, 159, 67, 0.12);
  border-radius: 6px;
  padding: 8px 12px;
}

.career-section-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.6;
}
.career-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}
.career-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(40, 199, 111, 0.12);
  border-radius: 999px;
  padding: 4px 11px 4px 9px;
  font-size: 12.5px;
  font-weight: 500;
}
.career-pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #28c76f;
  flex: none;
}
.career-pill time {
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
}
</style>
